import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import sideimg from "../../assets/images/faqs.jpg";
export default function CamundaFaqs() {
  const [openIndex, setOpenIndex] = useState(0);
  const faqs = [
    {
      q: "How does Truviq accelerate legacy BPM migrations to Camunda 8?",
      a: "Using our proprietary OrchestAI delivery engine, we automate workflow discovery, convert legacy rules to standard BPMN 2.0/DMN models, and scaffold external workers—reducing delivery time by up to 50%.",
    },
    {
      q: "Can you assist with Camunda 7 to Camunda 8 architecture migration?",
      a: "Yes. We handle Zeebe cloud/self-managed setups, refactor Java delegates into external workers, migrate runtime data, and optimize connector performance.",
    },
    {
      q: "How do you handle enterprise security and healthcare compliance?",
      a: "Our implementations incorporate SMART-on-FHIR, OAuth2 token handling, ISO-compliant BPMN modeling, and automated linting to enforce enterprise governance across every workflow.",
    },
    {
      q: "What post-launch support and managed operations do you provide?",
      a: "We offer continuous 24/7 SLA-backed managed services covering Zeebe broker tuning, incident triage, process analytics, and ongoing workflow optimization.",
    },
  ];

  return (
    <section className="w-full relative lg:py-24 md:py-16 py-10 px-6 sm:px-8 lg:px-12">
      <div
        className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
        style={{
          background:
            "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
        }}
      />
      <div className="3xl:w-4/5 xl:w-[90%] mx-auto space-y-16">
        <div className="flex flex-col items-center text-center md:mb-20 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full backdrop-blur-md shadow-xs mb-3"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600">
              CLEAR ANSWERS
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#000036]"
          >
            Frequently Asked{" "}
            <span className="italic font-serif font-normal text-[#5dc9f0]">
              Questions
            </span>{" "}
          </motion.h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-7 space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openIndex === idx;
              return (
                <div
                  key={idx}
                  className={`border rounded-2xl transition-all duration-200 overflow-hidden ${
                    isOpen
                      ? "bg-white border-slate-300 shadow-sm"
                      : "bg-white/60 border-slate-200/80 hover:border-slate-300"
                  }`}
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                    className="w-full p-6 text-left flex items-center justify-between gap-4 transition-colors"
                  >
                    <span className="text-base sm:text-lg font-bold text-[#000036] tracking-tight">
                      {faq.q}
                    </span>
                    <div
                      className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                        isOpen
                          ? "bg-[#000036] text-white"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {isOpen ? <Minus size={15} /> : <Plus size={15} />}
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                      >
                        <div className="px-6 pb-6 pt-1 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="relative rounded-xl  shadow-sm overflow-hidden space-y-6">
              <div className="relative w-full h-72 sm:h-80 rounded-xl overflow-hidden bg-slate-50 border border-slate-200/80 group">
                <img
                  src={sideimg}
                  alt="Camunda 8 Process Engine Architecture"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
